import React from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import AgentLegalForm from "../../../enum/AgentLegalForm.enum";
import Provider from "../../../enum/Provider.enum";
import {
  FieldColumn,
  FieldRow,
  Section,
  SectionTitle,
  StyledDatePicker,
} from "../styles";
import { AgentFormState } from "../../../types/agentTypes";

type ContractSectionProps = {
  provider: Provider;
  formData: AgentFormState;
  setFormData: React.Dispatch<React.SetStateAction<AgentFormState>>;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
};

const ContractSection = ({
  provider,
  formData,
  setFormData,
  onChange,
}: ContractSectionProps) => {
  const { t } = useTranslation();
  const requiresContractDate = [Provider.UAE, Provider.TFOS].includes(
    provider,
  );

  return (
    <Section>
      <SectionTitle>{t("createAgent.contract.title")}</SectionTitle>

      <FieldRow>
        <FieldColumn>
          <TextField
            size="small"
            label={t("createAgent.contract.number")}
            name="contract_number"
            value={
              formData.contract_number
                ? formData.contract_number.slice(0, 10)
                : ""
            }
            fullWidth
            required
            onChange={onChange}
          />
        </FieldColumn>

        <FieldColumn>
          <StyledDatePicker
            value={
              formData.contract_date ? dayjs(formData.contract_date) : null
            }
            label={t("createAgent.contract.date")}
            onChange={(date) => {
              const typedDate = date as Dayjs | null;

              setFormData((prev) => ({
                ...prev,
                contract_date: typedDate ? typedDate.format("YYYY-MM-DD") : "",
              }));
            }}
            format="DD-MM-YYYY"
            slotProps={{
              textField: {
                required: requiresContractDate,
                size: "small",
              },
            }}
            sx={{
              width: "100%",
              maxHeight: 41,
              boxSizing: "border-box",
              alignSelf: "en",
            }}
          />
        </FieldColumn>
      </FieldRow>

      {formData.format === AgentLegalForm.COMPANY && (
        <FieldRow>
          <FieldColumn>
            <TextField
              size="small"
              label={t("createAgent.contract.authorityBasis")}
              name="authority_basis"
              value={formData.authority_basis ?? ""}
              fullWidth
              required
              onChange={onChange}
            />
          </FieldColumn>

          <FieldColumn>
            <StyledDatePicker
              value={
                formData.data_authority_start
                  ? dayjs(formData.data_authority_start)
                  : null
              }
              label={t("createAgent.contract.authorityStartDate")}
              onChange={(date) => {
                const typedDate = date as Dayjs | null;

                setFormData((prev) => ({
                  ...prev,
                  data_authority_start: typedDate
                    ? typedDate.format("YYYY-MM-DD")
                    : "",
                }));
              }}
              maxDate={
                formData.data_authority_end
                  ? dayjs(formData.data_authority_end)
                  : undefined
              }
              format="DD-MM-YYYY"
              sx={{ height: 41 }}
            />
          </FieldColumn>

          <FieldColumn>
            <StyledDatePicker
              value={
                formData.data_authority_end
                  ? dayjs(formData.data_authority_end)
                  : null
              }
              label={t("createAgent.contract.authorityEndDate")}
              onChange={(date) => {
                const typedDate = date as Dayjs | null;

                setFormData((prev) => ({
                  ...prev,
                  data_authority_end: typedDate
                    ? typedDate.format("YYYY-MM-DD")
                    : "",
                }));
              }}
              minDate={
                formData.data_authority_start
                  ? dayjs(formData.data_authority_start)
                  : undefined
              }
              format="DD-MM-YYYY"
              sx={{ height: 41 }}
            />
          </FieldColumn>
        </FieldRow>
      )}
    </Section>
  );
};

export default ContractSection;
